/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { ReactNode, Suspense, useEffect } from 'react';

// React query
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@lib/react-query';

// Mantine
import {
	MantineProvider,
	ColorSchemeScript,
} from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { resolver, theme } from '@config/theme/theme';
import { DatesProvider } from '@mantine/dates';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/core/styles.layer.css';
import 'mantine-datatable/styles.layer.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';

// i18n
import '@config/i18n/config';
import { useTranslation } from 'react-i18next';

// Localstorage
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);
import 'dayjs/locale/fr';
dayjs.locale('fr');
import 'dayjs/locale/en';
dayjs.locale('en');
import utc from 'dayjs/plugin/utc';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { Loading } from '@components/atoms/Loading';
dayjs.extend(utc);

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
type AppProviderProps = {
	children: ReactNode;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const AppProvider = ({
	children,
}: AppProviderProps) => {
	/* ******************************** HOOKS ******************************* */
	const { i18n } = useTranslation();
	const [lng] = useLocalStorage('lng', 'fr');

	useEffect(() => {
		if (lng) i18n.changeLanguage(lng);
	}, [i18n, lng]);

	/* ******************************** RENDERING ******************************* */
	return (
		<MantineProvider
			theme={theme}
			cssVariablesResolver={resolver}
		>
			<ColorSchemeScript />
			<DatesProvider
				settings={{
					locale: i18n.language ? i18n.language : 'en',
				}}
			>
				<QueryClientProvider client={queryClient}>
					<ReactQueryDevtools initialIsOpen={false} />
					<Notifications />
					<Suspense fallback={<Loading />}>
						{children}
					</Suspense>
				</QueryClientProvider>
			</DatesProvider>
		</MantineProvider>
	);
};
